"use client";

import styles from './_version-2.module.scss';
import CustomImage from '@/components/image';
import Button from '@/components/button';
import PageAnchor from '@/components/page-anchor';

export default function HcpClinicalInformationDoseReductionsVersion2({ modalRef = null }) {

  return (
    <>
      <PageAnchor anchorId="trial-2-efficacy-dose-reductions" />
      <section className={styles.dose}>
        <div className={`container ${styles.dose_container}`}>
          <h2 className='page-heading h1'>
            GC dose reductions with CRENESSITY were durable through 2 years<sup>1,7,10</sup>
          </h2>
          <div className={styles.data}>
            <p className={styles.data_heading}>
              Change in daily GC dose (mean percent change from baseline)<sup className="!font-normal">1,7,10</sup><span className="super-asterisc">*</span><sup className="!font-normal">&dagger;</sup>
            </p>
            <div className={styles.data_grid}>
              <picture>
                <source
                  media="(max-width: 767px)"
                  srcSet="/images/page/hcp/clinical-information/Clinical-Information_Adult-GC-Dose-Reductions_mobile.svg"
                  width={327}
                  height={1643}
                />
                <CustomImage
                  className={styles.data_image}
                  src="/images/page/hcp/clinical-information/Clinical-Information_Adult-GC-Dose-Reductions.svg"
                  alt="GC dose reductions were reduced by 27% with CRENESSITY vs 10% with placebo, an ~2x greater reduction."
                  width={770}
                  height={519}
                />
              </picture>
              <div className={styles.data_aside}>
                <div className='hcp-callout'>
                  GC dose reductions were maintained in adults treated with CRENESSITY <strong>through 2 years</strong>.<sup className="!font-normal">1,7</sup>
                </div>
                <p>
                  Mean GC dose reduction at 2 years was ~38% for adults who continued on CRENESSITY (n=149).<sup className="!font-normal">7</sup>
                  <span>The exploratory endpoints were not adjusted for multiplicity; these descriptive results could represent chance findings.<sup className="!font-normal">25</sup></span>
                </p>
              </div>
              <div className={`hcp-callout ${styles.data_subgroup}`}>
                <p className="text-left">Consistent with the overall GC reduction endpoint, these subgroups favored CRENESSITY over placebo with greater dose reduction at week 24<sup className="!font-normal">8,¶</sup>:</p> 
                <ul>
                  <li>Sex</li>
                  <li>BMI (&lt;30 kg/m<sup className="!font-normal">2</sup> and ≥30 kg/m<sup className="!font-normal">2</sup>)</li>
                  <li>Baseline androstenedione levels (≤ULN and &gt;ULN)</li>
                  <li>Baseline GC dose (&lt;37 mg/day and ≥37 mg/day)</li>
                </ul>
              </div>
            </div>
          </div>

          <ul className={`footnotes ${styles.footnotes}`}>
            <li><span className="hang">*</span><span>Mean baseline GC doses in hydrocortisone equivalents were 32.3 ± 9.3 mg/day for all participants, 32.1 ± 9.5 mg/day for placebo, and 32.4 ± 9.2 mg/day for CRENESSITY.<sup>10</sup></span></li>
            <li><sup className="hang">&dagger;</sup><span>Endpoints are presented as LSM change from baseline at week 24 and as mean change at 1 year.<sup>10</sup></span></li>
            <li><sup className="hang">&Dagger;</sup><span>The 2 year data for GC reduction were determined without protocol-defined reference to androstenedione level at the visit.<sup>7</sup></span></li>
            <li><sup className="hang">&sect;</sup><span>LSMD was based on the ANCOVA model, which included baseline GC dose as a covariate and treatment group and stratification factors as fixed effects.<sup>8</sup></span></li>
          </ul>

          <div className={styles.ctaWrapper}>
            <Button
              type="button"
              variant={['outline--eggplant', 'hcp-big']} 
              onClick={() => modalRef?.current?.open()}
            >
              View high-dose GC effects
            </Button>
          </div>

          <div className="hcp-callout">
            <strong>96%</strong> of adults wished they could have started CRENESSITY at a younger age.<sup className="!font-normal">14</sup>*
          </div>
          <ul className={`footnotes ${styles.footnotes}`}>
            <li>
              <span className="hang">*</span>
              <span>
                Percentage based on adult participants (n=46/48) from the CAHtalyst<sup>&reg;</sup> trials who &ldquo;agreed&rdquo; or &ldquo;strongly agreed&rdquo; to the statement, &ldquo;I wish I could have started CRENESSITY at a younger age,&rdquo; in the final treatment satisfaction survey.<sup className="!font-normal">14</sup>
              </span>
            </li>
          </ul>
        </div>
      </section>
    </>
  );
}
